import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Handshake } from 'lucide-react'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { FilterBar, SearchInput } from '@/shared/ui/filters'
import { EmptyState, ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { usePoliticsCodes, CODE_LIST_UNAVAILABLE } from '@/features/polling/useVocabulary'
import { fmtDate } from '@/shared/lib/format'
import { listCoalitions, listParties } from './api'
import { CoalitionDrawer } from './CoalitionDrawer'
import { CoalitionFormDialog } from './CoalitionFormDialog'
import {
  EntityChip, Pill, Swatch, coalitionStatusLabel, coalitionStatusTone,
  count,
} from './vocab'

const PAGE_SIZE = 50
/** Enough for a national register; the pager owns the coalition list itself. */
const PARTY_LOOKUP_SIZE = 200
const CHIPS_SHOWN = 4

/**
 * The alliances.
 *
 * ## Where the member chips come from
 *
 * A coalition LIST row carries counts and no `members` — that array is on the
 * detail read only, and forty detail requests to paint one column is not a
 * column, it is a stampede. Every PARTY row, on the other hand, carries its own
 * open membership. So the chips are built from one parties request, grouped by
 * `coalition.coalitionId`, which also gets the chips the right colour: a
 * membership carries the COALITION's `colourHex`, never the party's.
 */
export function CoalitionsPage() {
  const canRead = useCan('politics:read')
  const canWrite = useCan('politics:coalition')

  const [params, setParams] = useSearchParams()
  // The code list is the server's; `vocab.tsx` only owns the English.
  const statuses = usePoliticsCodes('coalitionStatus')
  const q = params.get('q') ?? ''
  const status = params.get('status') ?? ''
  const page = Number(params.get('page') ?? 0)
  const openId = params.get('coalition')

  const [creating, setCreating] = useState(false)

  const list = useLoad(
    () => listCoalitions({ search: q || undefined, status: status || undefined }, page, PAGE_SIZE),
    [q, status, page])
  const parties = useLoad(() => listParties({}, 0, PARTY_LOOKUP_SIZE), [])

  if (!canRead) return <Forbidden what="the coalition register" />

  function patch(next: Record<string, string>) {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      // Page zero is the default, so it is absent from the URL rather than
      // spelled out — a shared link should not carry `?page=0`.
      if (v && !(k === 'page' && v === '0')) sp.set(k, v)
      else sp.delete(k)
    }
    if (!('page' in next)) sp.delete('page')
    setParams(sp, { replace: true })
  }

  const partyRows = parties.data?.content ?? []
  const partyColours: Record<string, string | null> = {}
  const membersByCoalition = new Map<string, typeof partyRows>()
  for (const p of partyRows) {
    partyColours[p.id] = p.colourHex
    const cid = p.coalition?.coalitionId
    if (!cid) continue
    membersByCoalition.set(cid, [...(membersByCoalition.get(cid) ?? []), p])
  }
  const partiesTruncated =
    (parties.data?.totalElements ?? 0) > partyRows.length

  const rows = list.data?.content ?? []
  const filtered = Boolean(q || status)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Coalitions"
        description="Alliances, who is in them today, and how many candidates reach them."
        right={canWrite && (
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            onClick={() => setCreating(true)}>
            New coalition
          </button>
        )}
      />

      <FilterBar activeCount={(q ? 1 : 0) + (status ? 1 : 0)}>
        <SearchInput value={q} onChange={(v) => patch({ q: v })} placeholder="Name or code…" />
        <select className="erp-input axis-full-sm" aria-label="Status" value={status}
          onChange={(e) => patch({ status: e.target.value })}>
          <option value="">Any status</option>
          {/* The served code list, never a literal written here: a transcribed
              list drifts silently the day the server adds a status. */}
          {statuses.codes.map((s) => (
            <option key={s} value={s}>{coalitionStatusLabel(s)}</option>
          ))}
        </select>
        {statuses.unavailable && (
          <span className="text-erp-xs text-red-700">{CODE_LIST_UNAVAILABLE}</span>
        )}
        {filtered && (
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm"
            onClick={() => patch({ q: '', status: '' })}>
            Clear filters
          </button>
        )}
      </FilterBar>

      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error ?? parties.error} />
        <ErpTable head={(
          <>
            <Th>Code</Th>
            <Th>Name</Th>
            <Th>Member parties</Th>
            <Th className="text-right">Candidates</Th>
            <Th>Status</Th>
            <Th className="axis-hide-sm">Formed</Th>
          </>
        )}>
          {list.loading && rows.length === 0 ? (
            <LoadingRows colSpan={6} label="Loading coalitions…" />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={6}>
                <EmptyState
                  icon={<Handshake className="h-4 w-4" />}
                  title={filtered ? 'No coalition matches this filter' : 'No coalitions yet'}
                  hint={filtered
                    ? 'Clear a filter to widen the search.'
                    : 'A coalition is optional — parties and candidates work without one. Create '
                      + 'it when an alliance actually exists, then record each party joining it.'}
                  action={filtered
                    ? (
                      <button type="button" className="erp-btn"
                        onClick={() => patch({ q: '', status: '' })}>Clear filters</button>
                    ) : canWrite ? (
                      <button type="button" className="erp-btn erp-btn-primary"
                        onClick={() => setCreating(true)}>Create a coalition</button>
                    ) : undefined}
                />
              </td>
            </tr>
          ) : rows.map((c) => {
            const members = membersByCoalition.get(c.id) ?? []
            return (
              <tr
                key={c.id}
                className="hover:bg-muted/40 cursor-pointer"
                tabIndex={0}
                onClick={() => patch({ coalition: c.id, page: String(page) })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') patch({ coalition: c.id, page: String(page) })
                }}
              >
                <Td>
                  <span className="inline-flex items-center gap-2">
                    <Swatch colourHex={c.colourHex} />
                    <span className="font-mono text-erp-xs">{c.code}</span>
                  </span>
                </Td>
                <Td className="font-medium">{c.name}</Td>
                <Td>
                  {members.length === 0 ? (
                    <span className="text-erp-xs text-muted-foreground">
                      {parties.loading ? 'Loading parties…' : 'No party has joined'}
                    </span>
                  ) : (
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {members.slice(0, CHIPS_SHOWN).map((p) => (
                        <EntityChip key={p.id} entity={p} />
                      ))}
                      {members.length > CHIPS_SHOWN && (
                        <span className="text-erp-xs text-muted-foreground tabular-nums">
                          +{members.length - CHIPS_SHOWN}
                        </span>
                      )}
                    </span>
                  )}
                </Td>
                <Td className="text-right tabular-nums">{count(c.candidateCount)}</Td>
                <Td>
                  <Pill tone={coalitionStatusTone(c.statusCode)}>
                    {coalitionStatusLabel(c.statusCode)}
                  </Pill>
                </Td>
                <Td className="axis-hide-sm whitespace-nowrap tabular-nums text-muted-foreground">
                  {fmtDate(c.formedOn)}
                </Td>
              </tr>
            )
          })}
        </ErpTable>

        <OffsetPager
          page={page}
          totalPages={list.data?.totalPages ?? 1}
          totalElements={list.data?.totalElements ?? 0}
          onPage={(next) => patch({ page: String(next) })}
          loading={list.loading}
          unit="coalitions"
        />
      </Panel>

      {partiesTruncated && (
        <Note tone="info">
          The member chips are built from the first {partyRows.length} parties of{' '}
          {parties.data?.totalElements}. Open a coalition to see its membership from the server
          rather than from this page's lookup.
        </Note>
      )}

      {/*
        * KEYED BY THE ROW, so React remounts it instead of reusing it.
        *
        * A drawer that stays mounted across rows keeps every piece of local
        * state from the last one: a half-open form, a typed value, and — worst —
        * the previous row's error. Opening party B after a refusal on party A
        * showed A's refusal under B's name, and the "Join" button toggled a form
        * that was already open from A, so it appeared to do nothing. One line
        * here is cheaper and more reliable than an effect that resets six
        * useStates and has to be extended every time a seventh is added.
        */}
      <CoalitionDrawer key={openId ?? 'none'}
        coalitionId={openId}
        partyColours={partyColours}
        onClose={() => patch({ coalition: '', page: String(page) })}
        onChanged={() => { list.reload(); parties.reload() }}
      />

      <CoalitionFormDialog
        open={creating}
        coalition={null}
        onClose={() => setCreating(false)}
        onSaved={(saved) => { setCreating(false); list.reload(); patch({ coalition: saved.id }) }}
      />
    </div>
  )
}
